<template>
  <action-message :on="showToast" :error="true" :message="toastMessage" />
  <div class="flex items-center space-x-4">
    <div>
      <div
        class="rounded-full w-12 h-12 flex justify-center items-center relative z-10"
        :class="defaultDeviceIconCssClass()"
      >
        <img :src="deviceIcon" :alt="deviceType" width="20" height="20" class="" />
      </div>
    </div>

    <div class="">
      <Menu as="div" class="relative inline-block text-left">
        <div>
          <MenuButton
            class="
              items-center
              w-16
              h-12
              rounded-full
              text-sm
              font-medium
              text-white
              devices-dropdown
              grid
              justify-items-end
            "
          >
            <ChevronDownIcon class="mr-1 ml-2 h-5 w-5" aria-hidden="true" />
          </MenuButton>
        </div>

        <transition>
          <MenuItems
            class="
              absolute
              left-0
              mt-2
              w-56
              origin-top-right
              rounded-md
              bg-white
              shadow-lg
              ring-1 ring-gray-300
              focus:outline-none
            "
            style="z-index: 100"
          >
            <div class="px-1 py-1">
              <MenuItem v-for="device in devices" :key="device.deviceId">
                <button
                  class="
                    p-2
                    flex
                    group
                    w-full
                    text-sm
                    rounded-md
                    justify-center
                    text-gray-900
                    hover:text-white
                    hover:bg-vice-teal-600
                  "
                  @click="selectDeviceHandler(device)"
                >
                  {{ device.label }}
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>
    <div class="">
      <div class="device-type-label-regular font-medium">{{ deviceType }}</div>
      <div class="device-label-regular font-medium">
        <span v-if="deviceLabel">{{ deviceLabel }}</span>
        <span v-else>Not Setup</span>
      </div>
    </div>
  </div>

  <!-- Mic level -->
  <div v-if="showTest && deviceKind === DEVICES_KINDS.AUDIO_INPUT" class="flex-col items-center">
    <span class="p-2 text-xs text-gray-400">Mic level</span>
    <div class="rounded overflow-hidden relative w-auto mx-1 h-0 pb-3 pt-3">
      <div
        v-if="audioStream?.active"
        class="
          absolute
          bg-white
          rounded-lg
          h-3
          left-0
          right-0
          top-1/2
          transform
          -translate-y-1/2
          overflow-hidden
          border border-gray-300
        "
      >
        <div
          class="absolute bg-vice-teal-400 rounded-lg h-full top-0 left-0"
          :style="{ width: `${currentWidth}%` }"
        />
      </div>
      <div
        v-if="!audioStream?.active"
        class="absolute top-0 left-0 w-full h-full flex flex-col ml-7 text-sm justify-center z-10"
      >
        <span class="flex items-center">
          <svg
            class="animate-spin -ml-1 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="#14B8A6" stroke-width="4" />
            <path
              class=""
              fill="#14B8A6"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>

  <!-- Speaker test -->
  <div v-if="showTest && deviceKind === DEVICES_KINDS.AUDIO_OUTPUT" class="flex-col">
    <audio ref="speakerTest" class="hidden" />
    <div class="rounded overflow-hidden relative w-auto mx-1 pb-1 flex items-center gap-3">
      <button
        @click="playSpeakerTest"
        :disabled="isSpeakerPlaying || !deviceLabel"
        class="
          w-36
          flex
          items-center
          gap-2
          px-3
          py-1.5
          text-xs
          font-medium
          rounded-lg
          border
          transition-all
          leading-6
        "
        :class="[
          !deviceLabel ? 'pointer-events-none opacity-60' : 'cursor-pointer',
          isSpeakerPlaying
            ? 'bg-vice-teal-50 border-vice-teal-400 text-vice-teal-600 cursor-not-allowed'
            : 'bg-white border-gray-300 text-gray-600 hover:border-vice-teal-400 hover:text-vice-teal-600',
        ]"
      >
        <span v-if="!isSpeakerPlaying">
          <!-- Speaker icon -->
          <span class="rounded-full w-6 h-6 flex justify-center items-center relative">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.7513 21.9753V19.884C16.6974 19.2649 18.2649 18.1406 19.4541 16.5109C20.6434 14.8815 21.2381 13.0445 21.2381 11C21.2381 8.95544 20.6434 7.11847 19.4541 5.48904C18.2649 3.8594 16.6974 2.73504 14.7513 2.11597V0.0246582C17.2629 0.697475 19.3093 2.04581 20.8903 4.06965C22.4712 6.0935 23.2616 8.40361 23.2616 11C23.2616 13.5963 22.4712 15.9065 20.8903 17.9303C19.3093 19.9542 17.2629 21.3025 14.7513 21.9753ZM0.740234 14.4068V7.66063H5.74791L11.5337 1.87479V20.1926L5.74791 14.4068H0.740234ZM14.7513 15.9635V6.03649C15.6612 6.53121 16.3643 7.22787 16.8608 8.12645C17.3571 9.02504 17.6053 9.99413 17.6053 11.0337C17.6053 12.0594 17.3549 13.0137 16.8541 13.8968C16.3533 14.7799 15.6524 15.4687 14.7513 15.9635ZM9.5099 6.78363L6.60905 9.68448H2.76374V12.3829H6.60905L9.5099 15.2838V6.78363Z"
                fill="#0d9488"
              />
            </svg>
          </span>
        </span>
        <span v-else>
          <!-- Visual bars while playing -->
          <span v-if="isSpeakerPlaying" class="flex items-end gap-0.5 h-4">
            <span
              v-for="i in 5"
              :key="i"
              class="w-1 bg-vice-teal-400 rounded-full animate-bounce"
              :style="{
                animationDelay: `${i * 0.1}s`,
                height: `${[60, 100, 80, 100, 60][i - 1]}%`,
              }"
            />
          </span>
        </span>
        {{ isSpeakerPlaying ? 'Playing...' : 'Speaker test' }}
      </button>
    </div>
  </div>
</template>
<script>
import { ref, watch, onMounted } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/solid';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import ActionMessage from '@/Jetstream/ActionMessage.vue';
import { CAMERA_TYPES, DEVICES_KINDS } from '@/Utils/vice-constants';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';

export default {
  name: 'DefaultDeviceSelector',
  props: {
    deviceIcon: {
      type: String,
      required: true,
    },
    deviceType: {
      type: String,
      required: true,
    },
    deviceKind: {
      type: String,
      required: true,
    },
    showTest: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChevronDownIcon,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    ActionMessage,
  },
  setup(props) {
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    let javascriptNode = null;
    const devices = ref([]);
    const currentWidth = ref(0);
    const showToast = ref(false);
    const audioStream = ref(null);
    const deviceLabel = ref(null);
    const isSpeakerPlaying = ref(false);
    const speakerTest = ref(null);
    const toastMessage = ref('Device adding error');

    onMounted(() => {
      const device = deviceAndSessionSetupStore.getClinicianConnectedDefaultDevice(
        props.deviceKind,
      );

      // Set device label to slot if device was saved in this slot
      if (device) {
        deviceLabel.value = device.label;

        if (props.deviceKind === DEVICES_KINDS.AUDIO_INPUT && props.showTest) {
          startMic(device);
        }
      }

      // get devices from storage if page was not reloaded
      // TODO: to understand in more detail
      devices.value = deviceAndSessionSetupStore.getConnectedDevices(props.deviceKind);
    });

    // watching for Pinia storage initialization to get devices list according to
    // device kind
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(props.deviceKind),
      (devicesList) => {
        devices.value = devicesList;
      },
    );

    const selectDeviceHandler = (device) => {
      axios
        .post(route('clinician.set-default-device'), {
          device_kind: props.deviceKind,
          device_data: device,
        })
        .then(() => {
          // add to pinia
          deviceAndSessionSetupStore.manageDefaultClinicianDevices(props.deviceKind, device);

          // save mic to device store
          if (props.deviceKind === DEVICES_KINDS.AUDIO_INPUT) {
            multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId =
              device.deviceId;

            // visualisation
            startMic(device);
          }
          // save speaker to device store
          if (props.deviceKind === DEVICES_KINDS.AUDIO_OUTPUT) {
            multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId =
              device.deviceId;
          }

          deviceLabel.value = device.label;
        })
        .catch((e) => {
          console.log(e);
          showToast.value = true;
        });
    };

    // get media and visualize
    async function startMic(mic) {
      try {
        if (audioStream?.value?.active) {
          await stopLiveStream(audioStream);
        }

        await new Promise((resolve) => {
          setTimeout(resolve, 100);
        });

        // Start audio
        await new Promise((resolve, reject) => {
          const constraints = { audio: { deviceId: { exact: mic.deviceId } }, video: false };
          navigator.mediaDevices
            .getUserMedia(constraints)
            .then(async (currentStream) => {
              if (audioStream.value) {
                await stopLiveStream(audioStream);
              }

              audioStream.value = currentStream;

              setTimeout(() => {
                if (
                  currentStream.active &&
                  currentStream.getAudioTracks().length > 0 &&
                  currentStream.getAudioTracks()[0].readyState === 'live'
                ) {
                  // Setup audio visualization
                  setupAudioVisualization(currentStream);
                  resolve();
                }
              }, 300);
            })
            .catch(reject);
        });
      } catch (error) {
        console.error('Microphone visualisation failed:', error);
      }
    }

    const stopLiveStream = async (liveStream) => {
      if (liveStream.value?.active) {
        await liveStream.value.getTracks().forEach((track) => {
          track.stop();
          liveStream.value.removeTrack(track);
        });
        liveStream.value = null;
      }
    };

    const setupAudioVisualization = (stream) => {
      try {
        const audioContext = new AudioContext();
        const microphone = audioContext.createMediaStreamSource(stream);

        if (javascriptNode) {
          javascriptNode.onaudioprocess = null;
          javascriptNode.disconnect();
        }

        javascriptNode = audioContext.createScriptProcessor(1024, 1, 1);
        microphone.connect(javascriptNode);
        javascriptNode.connect(audioContext.destination);

        let maxLevelL = 0.5;
        let oldLevelL = 0.1;

        javascriptNode.onaudioprocess = (event) => {
          const inputL = event.inputBuffer.getChannelData(0);
          let instantL;
          let sumL = 0.0;
          for (let i = 0; i < inputL.length; ++i) {
            sumL += inputL[i] * inputL[i];
          }
          instantL = Math.sqrt(sumL / inputL.length);
          maxLevelL = Math.max(maxLevelL, instantL);
          instantL = Math.max(instantL, oldLevelL - 0.008);
          oldLevelL = instantL;
          currentWidth.value = Math.max(0, Math.min(100, (instantL / maxLevelL) * 100));
        };
      } catch (error) {
        console.error('Audio visualization setup failed:', error);
      }
    };

    // Test speakers - play melody
    const playSpeakerTest = async () => {
      const speaker = deviceAndSessionSetupStore.getClinicianConnectedDefaultDevice(
        DEVICES_KINDS.AUDIO_OUTPUT,
      )?.deviceId;

      const audioEl = speakerTest.value;
      if (isSpeakerPlaying.value || speaker === null || !audioEl) return;
      isSpeakerPlaying.value = true;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      audioEl.srcObject = dest.stream;

      if (typeof audioEl.setSinkId === 'function' && speaker) {
        await audioEl.setSinkId(speaker).catch(console.warn);
      }

      await audioEl.play();

      const notes = [
        { freq: 523.25, duration: 0.15 },
        { freq: 659.25, duration: 0.15 },
        { freq: 783.99, duration: 0.15 },
        { freq: 659.25, duration: 0.15 },
        { freq: 523.25, duration: 0.25 },
      ];

      let startTime = ctx.currentTime + 0.05;

      notes.forEach(({ freq, duration }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(dest);

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);

        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.4, startTime + 0.02);
        gain.gain.linearRampToValueAtTime(0, startTime + duration);

        osc.start(startTime);
        osc.stop(startTime + duration);

        startTime += duration + 0.05;
      });

      const totalDuration = notes.reduce((acc, n) => acc + n.duration + 0.05, 0.05);

      setTimeout(() => {
        isSpeakerPlaying.value = false;
        audioEl.srcObject = null;
        ctx.close();
      }, totalDuration * 1000 + 100);
    };

    const defaultDeviceIconCssClass = () =>
      deviceLabel.value ? 'bg-vice-teal-600' : 'bg-gray-200';

    return {
      devices,
      showToast,
      speakerTest,
      audioStream,
      deviceLabel,
      currentWidth,
      toastMessage,
      isSpeakerPlaying,
      playSpeakerTest,
      selectDeviceHandler,
      defaultDeviceIconCssClass,
      DEVICES_KINDS,
    };
  },
};
</script>
<style scoped>
.device-type-label-regular {
  color: #881337;
  font-size: 16px;
}

.device-label-regular {
  font-size: 12px;
  color: #4b4b4b;
}
.devices-dropdown {
  background-color: #0f2630;
  margin-left: -55px;
}
</style>
